import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, AlertCircle, Clock, User, MapPin, Phone, FileText, Activity, Building2, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/Button';
import { Header } from '../components/Header';
import { BookingCard } from '../components/BookingCard';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { Modal } from '../components/Modal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ReviewModal } from '../components/ReviewModal';
import { useAuth } from '../context/AuthContext';
import { cancelBooking, getBookingDetails, getBookingHistory, submitReview } from '../services/api';
import { Booking, BookingStatus } from '../types';
import { ROUTES } from '../constants';

export const MyBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bookingId } = useParams<{ bookingId: string }>();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [totalBookings, setTotalBookings] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingBookingId, setCancellingBookingId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<BookingStatus | 'All'>('All');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingBooking, setReviewingBooking] = useState<Booking | null>(null);
  const [bookingsWithReviews, setBookingsWithReviews] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (user?.role === 'user' && !user.isProfileCompleted) {
      navigate(ROUTES.HOME_PAGE, { replace: true });
    }
  }, [user, navigate]);

  const fetchBookings = async (page: number = currentPage) => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await getBookingHistory({ page, limit: 10 });
      if (response.success) {
        const fetchedBookings = (response.data || []).map((b: Booking) => ({
          ...b,
          providerName: b.providerName || b.providerDetails?.name,
        }));
        setBookings(fetchedBookings);
        setTotalBookings(response.pagination.totalCount);
        setTotalPages(response.pagination.totalPages);
      } else {
        setErrorMessage(response.error || 'Failed to fetch bookings');
      }
    } catch (err) {
      setErrorMessage('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (bookingId && bookings.length > 0 && !showDetailsModal) {
      const bookingIdNum = parseInt(bookingId, 10);
      if (!isNaN(bookingIdNum)) {
        handleViewDetails(bookingIdNum);
      }
    }
  }, [bookingId, bookings]);

  const handleViewDetails = async (bookingId: number) => {
    const localBooking = bookings.find(b => b.bookingId === bookingId);
    if (localBooking) {
      setSelectedBooking({
        ...localBooking,
        providerName: localBooking.providerName || localBooking.providerDetails?.name,
      });
      setShowDetailsModal(true);
    }
    setActionLoading(true);
    try {
      const response = await getBookingDetails(bookingId);
      if (response.success && response.booking) {
        const details = response.booking;
        const providerName = details.providerDetails?.name || localBooking?.providerName;
        const providerIsBlocked = details.providerDetails?.isBlocked ?? localBooking?.providerIsBlocked ?? false;

        const merged: Booking = {
          ...localBooking,
          ...details,
          bookingId: details.bookingId ?? localBooking?.bookingId,
          serviceType: details.serviceType || localBooking?.serviceType,
          appointmentDate: details.appointmentDate || localBooking?.appointmentDate,
          status: details.status || localBooking?.status,
          providerName: providerName || localBooking?.providerName,
          providerDetails: details.providerDetails || localBooking?.providerDetails,
          providerIsBlocked,
        } as Booking;

        setSelectedBooking(merged);
        setShowDetailsModal(true);

        setBookings(prev =>
          prev.map(b =>
            b.bookingId === bookingId
              ? { ...b, providerName: providerName || b.providerName, providerIsBlocked }
              : b
          )
        );
      }
    } catch (err) {
      console.error('Failed to fetch booking details:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelClick = (bookingId: number) => {
    setCancellingBookingId(bookingId);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancellingBookingId) return;

    setActionLoading(true);
    setErrorMessage(null);
    try {
      const response = await cancelBooking(cancellingBookingId);
      if (response.success) {
        setSuccessMessage('Booking cancelled successfully');
        setShowCancelModal(false);
        setCancellingBookingId(null);
        await fetchBookings(currentPage);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setErrorMessage(response.message || 'Failed to cancel booking');
      }
    } catch (err) {
      setErrorMessage('Failed to cancel booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReviewClick = (bookingId: number) => {
    const booking = bookings.find(b => b.bookingId === bookingId);
    if (booking) {
      setReviewingBooking(booking);
      setShowReviewModal(true);
    }
  };

  const handleReviewSubmit = async (rating: number, comment: string) => {
    if (!reviewingBooking) return;

    try {
      const response = await submitReview({
        bookingId: reviewingBooking.bookingId,
        rating,
        comment,
      });

      if (response.success) {
        setSuccessMessage('Review submitted successfully');
        setBookingsWithReviews(prev => new Set(prev).add(reviewingBooking.bookingId));
        setShowReviewModal(false);
        setReviewingBooking(null);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        throw new Error(response.message || 'Failed to submit review');
      }
    } catch (err) {
      throw err;
    }
  };

  const filteredBookings = filterStatus === 'All'
    ? bookings
    : filterStatus === 'Accepted'
    ? bookings.filter(b => b.status === 'Accepted' || b.status === 'Approved')
    : bookings.filter(b => b.status === filterStatus);

  const getBookingCounts = () => {
    return {
      all: bookings.length,
      pending: bookings.filter(b => b.status === 'Pending').length,
      accepted: bookings.filter(b => b.status === 'Accepted' || b.status === 'Approved').length,
      completed: bookings.filter(b => b.status === 'Completed').length,
      cancelled: bookings.filter(b => b.status === 'Cancelled').length,
      rejected: bookings.filter(b => b.status === 'Rejected').length,
    };
  };

  const counts = getBookingCounts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Header theme="blue" showNotifications={true} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.HOME_PAGE)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Bookings</h1>
          <p className="text-gray-600">
            View and manage your care service bookings
            {totalBookings > 0 && (
              <span className="ml-2 text-sm font-medium text-blue-600">
                ({filteredBookings.length} of {totalBookings} total bookings)
              </span>
            )}
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
            <Button
              variant="ghost"
              onClick={() => fetchBookings(currentPage)}
              className="text-red-600 hover:text-red-700 text-sm"
            >
              Retry
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilterStatus('All')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'All'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            All ({counts.all})
          </button>
          <button
            onClick={() => setFilterStatus('Pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Pending ({counts.pending})
          </button>
          <button
            onClick={() => setFilterStatus('Accepted')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Accepted'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Approved ({counts.accepted})
          </button>
          <button
            onClick={() => setFilterStatus('Completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Completed'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Completed ({counts.completed})
          </button>
          <button
            onClick={() => setFilterStatus('Cancelled')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Cancelled'
                ? 'bg-gray-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Cancelled ({counts.cancelled})
          </button>
          <button
            onClick={() => setFilterStatus('Rejected')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Rejected'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Not Approved ({counts.rejected})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {filterStatus === 'All' ? 'No bookings yet' : `No ${filterStatus.toLowerCase()} bookings`}
            </h3>
            <p className="text-gray-600 mb-6">
              {filterStatus === 'All'
                ? 'Start by finding and booking a care provider'
                : `You don't have any ${filterStatus.toLowerCase()} bookings at the moment`}
            </p>
            <Button onClick={() => navigate(ROUTES.FIND_PROVIDERS)}>
              Find Providers
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <BookingCard
                key={booking.bookingId}
                booking={booking}
                userRole="service_user"
                onViewDetails={handleViewDetails}
                onCancel={handleCancelClick}
                onReview={handleReviewClick}
                providerName={booking.providerName || booking.providerDetails?.name || `Provider #${booking.providerId}`}
                hasReview={bookingsWithReviews.has(booking.bookingId)}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between mt-8 bg-white rounded-xl border border-gray-200 px-6 py-4">
            <p className="text-sm text-gray-600">
              Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
              <span className="font-semibold text-gray-900">{totalPages}</span>
              <span className="ml-2 text-gray-400">({totalBookings} total)</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>

      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Booking Details"
      >
        {selectedBooking && (
          <div className="space-y-6">
            <div className={`rounded-xl p-6 ${
              selectedBooking.status === 'Accepted' ? 'bg-blue-50 border-2 border-blue-200' :
              selectedBooking.status === 'Pending' ? 'bg-yellow-50 border-2 border-yellow-200' :
              selectedBooking.status === 'Rejected' ? 'bg-red-50 border-2 border-red-200' :
              selectedBooking.status === 'Completed' ? 'bg-green-50 border-2 border-green-200' :
              'bg-gray-50 border-2 border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Booking ID</p>
                  <p className="text-2xl font-bold text-gray-900">#{selectedBooking.bookingId}</p>
                </div>
                <BookingStatusBadge status={selectedBooking.status} />
              </div>

              <div className="grid grid-cols-1 gap-4 pt-4 border-t border-gray-300">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedBooking.status === 'Accepted' ? 'bg-blue-100' :
                    selectedBooking.status === 'Pending' ? 'bg-yellow-100' :
                    selectedBooking.status === 'Rejected' ? 'bg-red-100' :
                    selectedBooking.status === 'Completed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    <Activity className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Service Type</p>
                    <p className="font-semibold text-gray-900">{selectedBooking.serviceType}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedBooking.status === 'Accepted' ? 'bg-blue-100' :
                    selectedBooking.status === 'Pending' ? 'bg-yellow-100' :
                    selectedBooking.status === 'Rejected' ? 'bg-red-100' :
                    selectedBooking.status === 'Completed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    <Calendar className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Appointment Date</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(selectedBooking.appointmentDate).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedBooking.status === 'Accepted' ? 'bg-blue-100' :
                    selectedBooking.status === 'Pending' ? 'bg-yellow-100' :
                    selectedBooking.status === 'Rejected' ? 'bg-red-100' :
                    selectedBooking.status === 'Completed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    <Clock className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(selectedBooking.appointmentDate).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {selectedBooking.providerDetails && (
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 border border-teal-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-6 h-6 text-teal-700" />
                  <h3 className="text-lg font-bold text-gray-900">Provider Information</h3>
                  {selectedBooking.providerDetails.isVerified && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-teal-600 text-white rounded-full text-xs font-medium">
                      <ShieldCheck className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                </div>

                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Name</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.providerDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="font-semibold text-gray-900 break-all">{selectedBooking.providerDetails.email}</p>
                    </div>
                    {selectedBooking.providerDetails.city && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">City</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.providerDetails.city}</p>
                      </div>
                    )}
                    {selectedBooking.providerDetails.location && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Location</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.providerDetails.location}</p>
                      </div>
                    )}
                  </div>

                  {selectedBooking.providerDetails.typeOfCare && selectedBooking.providerDetails.typeOfCare.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Care Services Offered</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.providerDetails.typeOfCare.map((type, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 border border-teal-200">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedBooking.providerDetails.credentials && selectedBooking.providerDetails.credentials.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Credentials</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.providerDetails.credentials.map((cred, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                            {cred}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedBooking.patientSnapshot && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-6 h-6 text-blue-700" />
                  <h3 className="text-lg font-bold text-gray-900">Patient Information</h3>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Full Name</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.recipientName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Age</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.recipientAge} years</p>
                    </div>
                    {selectedBooking.patientSnapshot.recipientGender && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Gender</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.recipientGender}</p>
                      </div>
                    )}
                    {selectedBooking.patientSnapshot.mobilityStatus && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Mobility Status</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.mobilityStatus}</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Service Address</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedBooking.patientSnapshot.address}
                        </p>
                        <p className="text-sm text-gray-600">{selectedBooking.patientSnapshot.city}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-red-700" />
                      <p className="text-xs font-semibold text-red-700 uppercase">Emergency Contact</p>
                    </div>
                    <div>
                      <p className="text-xs text-red-600 mb-1">Name</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.emergencyContactName}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedBooking.notes && (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Additional Notes</h3>
                </div>
                <p className="text-gray-700 leading-relaxed bg-white rounded-lg p-4 border border-gray-200">
                  {selectedBooking.notes}
                </p>
              </div>
            )}

            {selectedBooking.rejectionReason && (
              <div className="bg-red-50 rounded-xl p-5 border-2 border-red-300">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-700" />
                  <h3 className="font-semibold text-red-900">Reason for Not Approving</h3>
                </div>
                <p className="text-red-800 leading-relaxed bg-white rounded-lg p-4 border border-red-200">
                  {selectedBooking.rejectionReason}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Booking"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to cancel this booking? This action cannot be undone.
          </p>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              className="flex-1"
              disabled={actionLoading}
            >
              No, Keep It
            </Button>
            <Button
              variant="primary"
              onClick={handleCancelConfirm}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  Cancelling...
                </span>
              ) : (
                'Yes, Cancel Booking'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {reviewingBooking && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
            setReviewingBooking(null);
          }}
          onSubmit={handleReviewSubmit}
          bookingId={reviewingBooking.bookingId}
          providerName={reviewingBooking.providerName || `Provider #${reviewingBooking.providerId}`}
        />
      )}
    </div>
  );
};
